"use client";
import React, { useCallback, useEffect, useRef, useState } from "react";
import type { ComponentType, SVGProps } from "react";
import AppImage from "@/components/ui/AppImage";
import { useHomeLanguage } from "./HomeLanguageContext";
import {
  BanknotesIcon,
  BuildingOffice2Icon,
  CubeTransparentIcon,
  ShieldCheckIcon,
  StarIcon,
} from "@heroicons/react/24/solid";

const reviewItems = [
  {
    name: "M. Fajar Aditia",
    role: "Co-Founder Omegatronik",
    rating: 5,
    quote: "HRM by Kolabo benar-benar ngebantu tim kami. Absensi, approval, sampai monitoring kerja jadi jauh lebih rapi dan nggak makan waktu."
  },
  {
    name: "Mumtaz Afif Saleh",
    role: "Account Manager RAD Creative",
    rating: 5,
    quote: "Yang paling kerasa itu semua proses internal jadi satu pintu. Dari absensi, reimburse, data karyawan, aset, sampai project management, jadi lebih enak dipantau."
  },
  {
    name: "Deddy Sujana",
    role: "User Kolabo",
    rating: 4,
    quote: "Praktis. Tim jadi kerja lebih cepat dan koordinasi nggak ribet."
  },
  {
    name: "Trisno Cahyo",
    role: "User Kolabo",
    rating: 4,
    quote: "Setelah pakai Kolabo, progress kerja tim lebih jelas dan laporan lebih gampang dicek."
  }
];

const reviewTickerId = [
  { text: "Supportnya cepat dan solutif", rating: 5 },
  { text: "Implementasi tim kami jadi jauh lebih rapi", rating: 5 },
  { text: "Approval dan monitoring kerja lebih gampang", rating: 4 },
  { text: "Data lintas divisi jadi lebih sinkron", rating: 5 },
  { text: "Lebih hemat biaya dibanding banyak tools terpisah", rating: 4 },
  { text: "Onboarding dibantu sampai tim siap jalan", rating: 5 },
];

const reviewTickerEn = [
  { text: "Fast and solution-oriented support", rating: 5 },
  { text: "Our implementation became much more organized", rating: 5 },
  { text: "Approvals and work monitoring are easier", rating: 4 },
  { text: "Cross-division data is now more synchronized", rating: 5 },
  { text: "More cost-efficient than using many separate tools", rating: 4 },
  { text: "Great onboarding support until the team is fully ready", rating: 5 },
];

const videoReviews = [
  {
    title: "Testimonial Kolabo X Rampai Group",
    desc:
      "Pengalaman penggunaan Kolabo secara langsung untuk operasional harian tim, mulai dari HRM, reimbursement, data karyawan, hingga project management dalam satu ekosistem.",
    src: "https://www.kolabo.id/assets/Testimonial%20Kolabo%20X%20Rampai%20Group.mp4?v=1",
    poster: "/assets/images/testimonials/rampai-review-cover.jpg",
  },
  {
    title: "Customer Review Kolabo X Fantech",
    desc:
      "Kolabo membantu operasional Fantech menjadi lebih terstruktur lewat integrasi HRM, kolaborasi tim, dan proses bisnis dalam satu platform.",
    src: "https://www.kolabo.id/assets/Kolabo%20jadi%20Game%20Changer%20buat%20Fantech.mp4",
    poster: "/assets/images/testimonials/fantech-review-cover.jpg",
  },
];

const clientLogos = [
  { src: "/assets/optimized/client1-a6146b171c.webp", name: "Partner 1" },
  { src: "/assets/optimized/client2-a88f7d916a.webp", name: "Partner 2" },
  { src: "/assets/optimized/client03-29c28f1c22.webp", name: "Partner 3" },
  { src: "/assets/optimized/client04-c56d5269c8.webp", name: "Partner 4" },
  { src: "/assets/optimized/client05-be30876c7b.webp", name: "Partner 5" },
  { src: "/assets/optimized/client06-8ea6beba43.webp", name: "Partner 6" },
  { src: "/assets/optimized/client07-5c6fe1f4ad.webp", name: "Partner 7" },
  { src: "/assets/optimized/client01-521a68f568.webp", name: "Partner 8" },
  { src: "/assets/optimized/client02-a6213b6694.webp", name: "Partner 9" },
  { src: "/assets/optimized/client08-551640c51c.webp", name: "Partner 10" },
  { src: "/assets/optimized/client3-541c74a525.webp", name: "Partner 11" },
  { src: "/assets/optimized/client4-b1f76fb373.webp", name: "Partner 12" },
  { src: "/assets/optimized/client5-f3a54cdb18.webp", name: "Partner 13" },
  { src: "/assets/optimized/client6-f6c596ae99.webp", name: "Partner 14" },
  { src: "/assets/optimized/client7-bb12a80bb6.webp", name: "Partner 15" },
  { src: "/assets/optimized/client8-52a584edcd.webp", name: "Partner 16" },
  { src: "/assets/optimized/client9-46489c713d.webp", name: "Partner 17" },
  { src: "/assets/optimized/client10-c9970a86df.webp", name: "Partner 18" },
  { src: "/assets/optimized/client11-9c04c44a6e.webp", name: "Partner 19" },
  { src: "/assets/optimized/client12-24b3b2eec9.webp", name: "Partner 20" },
  { src: "/assets/optimized/client13-a9eea77491.webp", name: "Partner 21" },
  { src: "/assets/optimized/client14-d1980d1a5d.webp", name: "Partner 22" },
];

type StatItem = {
  value: string;
  label: string;
  color: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
};

const stats: StatItem[] = [
  { value: "300+", label: "Perusahaan Aktif", Icon: BuildingOffice2Icon, color: "from-white to-white" },
  { value: "Ratusan", label: "Modul Operasional", Icon: CubeTransparentIcon, color: "from-white to-white" },
  { value: "24/7", label: "Dedicated Support", Icon: ShieldCheckIcon, color: "from-white to-white" },
  { value: "Rp500rb", label: "Mulai per Bulan", Icon: BanknotesIcon, color: "from-white to-white" },
];

export default function SocialProof() {
  const { lang } = useHomeLanguage();
  const sectionRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([]);
  const [activeVideoIdx, setActiveVideoIdx] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("revealed");
        });
      },
      { threshold: 0.08 }
    );
    sectionRef?.current?.querySelectorAll(".animate-on-scroll")?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  const totalVideoSlides = videoReviews.length;
  const isId = lang === "id";
  const reviewTicker = isId ? reviewTickerId : reviewTickerEn;

  const goToVideo = useCallback((idx: number) => {
    if (idx < 0) {
      setActiveVideoIdx(totalVideoSlides - 1);
      return;
    }
    setActiveVideoIdx(idx % totalVideoSlides);
  }, [totalVideoSlides]);

  const nextVideo = useCallback(() => {
    setActiveVideoIdx((prev) => (prev + 1) % totalVideoSlides);
  }, [totalVideoSlides]);

  const prevVideo = useCallback(() => {
    setActiveVideoIdx((prev) => (prev - 1 + totalVideoSlides) % totalVideoSlides);
  }, [totalVideoSlides]);

  useEffect(() => {
    videoRefs.current.forEach((video, idx) => {
      if (video && idx !== activeVideoIdx) {
        video.pause();
      }
    });
  }, [activeVideoIdx]);

  return (
    <section className="py-24 lg:py-28 bg-white relative overflow-hidden" ref={sectionRef}>
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-primary/[0.02] rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-secondary/[0.02] rounded-full blur-3xl" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-14 animate-on-scroll">
          <span className="section-label mb-6 inline-flex">
            <span className="w-1.5 h-1.5 rounded-full bg-primary pulse-dot inline-block" />
            {isId ? "Testimonials" : "Testimonials"}
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-[3.25rem] font-bold text-fg leading-[1.1] tracking-[-0.03em] mb-5 mt-5">
            {isId ? "Apa Kata Mereka" : "What They Say"}
          </h2>
          <p className="text-fg-muted text-[17px] max-w-2xl mx-auto leading-relaxed">
            {isId
              ? "Kolabo sudah menjadi solusi bagi banyak tim dan perusahaan dari berbagai industri."
              : "Kolabo has become a reliable solution for many teams and companies across industries."}
          </p>
        </div>

        {/* Review volume gimmick */}
        <div className="mb-8 animate-on-scroll">
          <div className="rounded-2xl border border-stone-200 bg-white shadow-card p-3 sm:p-4">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2.5">
                <div className="flex -space-x-2">
                  {clientLogos.slice(0, 5).map((logo, idx) => (
                    <div
                      key={`${logo.src}-${idx}`}
                      className="w-7 h-7 rounded-full border-2 border-white bg-white overflow-hidden shadow-sm"
                    >
                      <AppImage
                        src={logo.src}
                        alt={logo.name}
                        width={28}
                        height={28}
                        className="w-full h-full object-contain p-0.5"
                        sizes="28px"
                      />
                    </div>
                  ))}
                </div>
                <p className="text-xs sm:text-sm font-semibold text-fg">
                  {isId ? "Highlight dari banyak review customer aktif" : "Highlights from many active customer reviews"}
                </p>
              </div>

              <div className="review-volume-badge inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-[11px] sm:text-xs font-bold text-emerald-700 uppercase tracking-[0.08em]">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 pulse-dot" />
                {isId ? "100+ review lainnya" : "100+ more reviews"}
              </div>
            </div>

            <div className="relative overflow-hidden rounded-xl border border-stone-100 bg-stone-50">
              <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-stone-50 to-transparent z-10 pointer-events-none" />
              <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-stone-50 to-transparent z-10 pointer-events-none" />
              <div className="review-marquee-track py-2">
                {[...reviewTicker, ...reviewTicker].map((item, idx) => (
                  <div key={`${item.text}-${idx}`} className="px-2 shrink-0">
                    <div className="inline-flex items-center gap-2 rounded-full bg-white border border-stone-200 px-3 py-1.5">
                      <div className="flex items-center text-amber-500 gap-0.5">
                        {Array.from({ length: item.rating }).map((_, star) => (
                          <StarIcon key={star} className="w-3.5 h-3.5" aria-hidden="true" />
                        ))}
                      </div>
                      <span className="text-[12px] text-fg/80 font-medium whitespace-nowrap">
                        "{item.text}"
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Video reviews slider */}
        <div className="mb-10 animate-on-scroll">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs uppercase tracking-[0.14em] font-bold text-primary">{isId ? "Video Review" : "Video Reviews"}</p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={prevVideo}
                className="w-9 h-9 rounded-full border border-stone-200 bg-white text-fg/80 hover:text-fg hover:border-primary/30 transition-all"
                aria-label={isId ? "Video sebelumnya" : "Previous video"}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="mx-auto">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <button
                type="button"
                onClick={nextVideo}
                className="w-9 h-9 rounded-full border border-stone-200 bg-white text-fg/80 hover:text-fg hover:border-primary/30 transition-all"
                aria-label={isId ? "Video berikutnya" : "Next video"}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="mx-auto">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-2xl">
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${activeVideoIdx * 100}%)` }}
            >
              {videoReviews.map((review, idx) => (
                <div key={review.src} className="w-full shrink-0">
                  <div className="grid lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-7 rounded-2xl border border-stone-200 bg-stone-100 shadow-card p-3 sm:p-4">
                      <div className="rounded-xl bg-black flex items-center justify-center min-h-[320px] sm:min-h-[420px] lg:min-h-[520px] overflow-hidden">
                        <video
                          ref={(el) => {
                            videoRefs.current[idx] = el;
                          }}
                          controls
                          preload="none"
                          playsInline
                          poster={review.poster}
                          className="w-auto h-full max-h-[520px] max-w-full object-contain"
                        >
                          <source src={review.src} type="video/mp4" />
                        </video>
                      </div>
                    </div>
                    <div className="lg:col-span-5 rounded-2xl border border-stone-200 bg-white p-6 sm:p-7 shadow-card flex flex-col justify-center">
                      <h3 className="font-heading text-2xl font-bold text-fg tracking-tight mb-3">{review.title}</h3>
                      <p className="text-fg-muted text-[15px] leading-relaxed">
                        {isId
                          ? review.desc
                          : review.title.includes("Fantech")
                          ? "Kolabo helps Fantech run operations in a more structured way through integrated HRM, team collaboration, and connected business workflows."
                          : "A direct user story on how Kolabo supports day-to-day operations from HRM and reimbursements to employee data and project management in one ecosystem."}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 flex items-center justify-center gap-2.5">
            {videoReviews.map((review, idx) => (
              <button
                key={review.src}
                type="button"
                onClick={() => goToVideo(idx)}
                aria-label={`${isId ? "Pilih video review" : "Select review video"} ${idx + 1}`}
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  idx === activeVideoIdx ? "w-7 bg-primary" : "w-2.5 bg-stone-300 hover:bg-stone-400"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Testimonial cards */}
        <div className="mb-4 flex items-center justify-between gap-3 animate-on-scroll">
          <p className="text-xs uppercase tracking-[0.14em] font-bold text-primary">
            {isId ? "Pilihan Review" : "Featured Reviews"}
          </p>
          <p className="text-[12px] text-fg-muted">
            {isId ? "Menampilkan sebagian kecil review terbaru" : "Showing a small sample of recent reviews"}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-14 animate-on-scroll stagger-children">
          {reviewItems.map((item, i) => (
            <div
              key={i}
              className="rounded-2xl overflow-hidden border border-stone-200 bg-white shadow-card spotlight-card bento-card p-5"
            >
              <div className="flex items-start gap-4">
                <div className="min-w-0">
                  <p className="text-fg font-semibold text-base tracking-tight">{item.name}</p>
                  <p className="text-fg-muted text-sm">{item.role}</p>
                  <div className="flex items-center gap-0.5 text-amber-500 mt-1.5" aria-label={`rating ${item.rating}`}>
                    {Array.from({ length: item.rating }).map((_, star) => <StarIcon key={star} className="w-4 h-4" aria-hidden="true" />)}
                  </div>
                </div>
              </div>
              <p className="text-fg-muted text-[15px] leading-relaxed mt-4">"{item.quote}"</p>
            </div>
          ))}
        </div>

        {/* Stats — horizontal cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14 animate-on-scroll stagger-children">
          {stats.map((stat, i) => {
            const Icon = stat.Icon;
            return (
            <div
              key={i}
              className={`bg-gradient-to-br ${stat.color} rounded-xl p-6 border border-stone-200 text-center spotlight-card bento-card relative overflow-hidden group`}
            >
              <div className="relative z-10">
                <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/15 mx-auto mb-3 flex items-center justify-center">
                  <Icon className="w-6 h-6 text-primary" aria-hidden="true" />
                </div>
                <div className="font-heading text-[clamp(1.9rem,7.2vw,2.2rem)] sm:text-3xl font-bold gradient-text mb-1.5 tracking-[-0.02em] leading-none whitespace-nowrap inline-block px-[0.02em]">
                  {stat.value}
                </div>
                <div className="text-fg-muted text-xs font-bold uppercase tracking-[0.12em]">
                  {isId
                    ? stat.label
                    : i === 0
                    ? "Active Companies"
                    : i === 1
                    ? "Operational Modules"
                    : i === 2
                    ? "Dedicated Support"
                    : "Starting per Month"}
                </div>
              </div>
            </div>
            );
          })}
        </div>

        <div className="mb-12 animate-on-scroll">
          <div className="rounded-2xl overflow-hidden border border-stone-200 shadow-card bg-white">
            <AppImage
              src="/assets/optimized/kolabo-press-release-7f0830f130.webp"
              alt="Kolabo press release"
              width={1400}
              height={560}
              className="w-full h-auto"
              sizes="(max-width: 1280px) 100vw, 1280px"
            />
          </div>
        </div>

        {/* Trusted clients — infinite marquee */}
        <div className="animate-on-scroll">
          <div className="text-center mb-10">
            <p className="text-fg-muted/60 text-xs font-bold uppercase tracking-[0.18em]">
              {isId ? "Dipercaya oleh 300+ perusahaan di Indonesia" : "Trusted by 300+ companies in Indonesia"}
            </p>
            <div className="mt-3 mx-auto w-10 h-0.5 rounded-full bg-gradient-brand" />
          </div>

          {/* Marquee container */}
          <div className="relative overflow-hidden">
            {/* Fade edges */}
            <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />

            <div className="client-marquee-track">
              {[...clientLogos, ...clientLogos]?.map((logo, i) => (
                <div
                  key={i}
                  className="flex items-center justify-center px-6 shrink-0"
                >
                  <div className="flex items-center justify-center w-[152px] h-[76px] sm:w-[170px] sm:h-[86px] bg-white rounded-lg border border-stone-100 shadow-sm hover:shadow-md hover:border-primary/20 transition-all duration-300 px-4 py-2.5">
                    <AppImage
                      src={logo?.src}
                      alt={`Logo klien Kolabo - ${logo?.name}`}
                      width={148}
                      height={64}
                      className="w-auto h-auto max-w-[138px] max-h-[62px] sm:max-w-[152px] sm:max-h-[68px] object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                      sizes="(max-width: 640px) 138px, 152px"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
